import React from "react";
import styles from "./Accordion.module.css";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { loginSliceValue } from "../../redux/reducers/loginSlice";
import { INoteReq, IPatient, INote } from "../../hooks/patients/index.type";
import { ViewNotesModalState } from "../../pageComponents/Patients/ViewNotesModal";
import { useAddNote, useFetchNotes, useUpdateNote } from "../../hooks/patients";
import { Button } from "../Modal";
import CardWrapper from "../CardWrapper";
import AddNotesModal from "../../pageComponents/Patients/AddNotesModal";
import { IAddNoteStates } from "../../pageComponents/Patients/AddNotesModal";
import ViewNotesModal from "../../pageComponents/Patients/ViewNotesModal";
import NoteSkeleton from "../Skeletons/NoteSkeleton";
import classes from "../../styles/CustomStyles.module.css";
import useNoteTypeOptions from "../../hooks/useNoteTypeOptions";

interface NoteCardProps {
  title?: string;
  columns?: number;
  patientData?: IPatient;
  showAddNoteModal?: boolean;
  setShowAddNoteModal: React.Dispatch<React.SetStateAction<boolean>>;
  triggeredBy?: string;
  setTriggeredBy: React.Dispatch<React.SetStateAction<string>>;
  billingSessionId?: string;
}

const NoteCard: React.FC<NoteCardProps> = ({
  title = " Notes",
  columns = 10,
  patientData,
  showAddNoteModal,
  setShowAddNoteModal,
  triggeredBy,
  setTriggeredBy,
  billingSessionId,
}) => {
  const initialNoteStates: IAddNoteStates = {
    noteCategory: { value: "clinical", error: "" },
    noteContent: { value: "", error: "" },
  };
  const [noteModalStates, setNoteModalStates] =
    React.useState<IAddNoteStates>(initialNoteStates);
  const [viewNoteModal, setViewNotemodal] = React.useState<ViewNotesModalState>(
    { status: false }
  );
  const { userDetails } = useSelector(loginSliceValue);
  const { fetchedNotes, getNotes, fetchingNotes, hasMore } = useFetchNotes({
    patientId: patientData?._id,
  });
  const { addNote, addNoteLoading } = useAddNote({
    patientId: patientData?._id,
  });
  const { updateNote } = useUpdateNote({
    patientId: patientData?._id,
  });

  const {
    noteTypeOptions,
    loading: noteTypesLoading,
    error: noteTypesError,
  } = useNoteTypeOptions();

  const notesContainerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (patientData?._id) {
      getNotes(true, "desc");
    }
  }, [patientData?._id]);

  // Refresh notes when timer is stopped and note is added
  React.useEffect(() => {
    if (triggeredBy === "stop" && patientData?._id) {
      getNotes(true, "desc");
    }
  }, [triggeredBy, patientData?._id]);

  // const handleScroll = React.useCallback(
  //   (e: React.UIEvent<HTMLDivElement>) => {
  //     const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

  //     if (
  //       scrollHeight - scrollTop <= clientHeight * 1.5 &&
  //       !fetchingNotes &&
  //       hasMore
  //     ) {
  //       getNotes(false);
  //     }
  //   },
  //   [fetchingNotes, hasMore, getNotes]
  // );

  const handleSaveNote = async (
    noteText: string,
    note: INote
  ): Promise<
    { status: string; data?: { note?: Array<INote> } } | undefined
  > => {
    if (!note) return;

    try {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = noteText;
      const plainText = tempDiv.textContent || tempDiv.innerText || "";

      const formData: INoteReq & { _id: string } = {
        richText: noteText,
        noteCategory: note.noteCategory as
          | "clinical"
          | "non-clinical"
          | "timer-summary",
        _id: note._id,
        note: plainText,
        isImportant: note.isImportant || "no",
        isPhoneCall: note.isPhoneCall === "yes" ? "yes" : "no",
      };
      if (formData.noteCategory === "timer-summary") {
        formData.billingSessionId = billingSessionId || "";
      }

      if (note.noteCategory === "clinical" && note.type) {
        formData.type = note.type;
      }
      const response = await updateNote(formData);
      if (response?.status === "success") {
        await getNotes(true, "desc");
        return response;
      }
      return response;
    } catch (error) {
      console.error("Error saving note:", error);
      throw error;
    }
  };

  const handleAddNote = async (
    event: React.FormEvent<HTMLFormElement>
  ): Promise<{ status: string; data?: any } | undefined> => {
    event.preventDefault();

    try {
      if (!userDetails.data?.user?._id) {
        throw new Error("User not authenticated");
      }

      const formData = new FormData(event.currentTarget);
      const noteContent = formData.get("noteContent") as string;

      const noteTypeValue = (formData.get("noteType") ||
        formData.get("type") ||
        "general") as string;
      const noteType:
        | "general"
        | "care-plan"
        | "timer"
        | "treatment-plan-adherence-note"
        | "other-note-type" = (
        [
          "general",
          "care-plan",
          "timer",
          "treatment-plan-adherence-note",
          "other-note-type",
        ].includes(noteTypeValue)
          ? noteTypeValue
          : "general"
      ) as any;

      const isImportant =
        formData.get("isImportant") === "yes"
          ? ("yes" as const)
          : ("no" as const);
      if (!noteContent?.trim()) {
        setNoteModalStates((prevVal) => ({
          ...prevVal,
          noteContent: {
            ...prevVal.noteContent,
            error: "Note content cannot be empty",
          },
        }));
        return;
      }
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = noteContent;
      const plainText = tempDiv.textContent || tempDiv.innerText || "";
      const noteCategory =
        (formData.get("noteCategory") as
          | "clinical"
          | "non-clinical"
          | "timer-summary") || "clinical";

      const noteData: INoteReq = {
        ...(noteCategory === "clinical" && { type: noteType }),
        note: plainText,
        richText: noteContent,
        noteCategory,
        isImportant,
      };
      if (noteData.noteCategory === "timer-summary") {
        noteData.billingSessionId = billingSessionId ?? "";
      }

      const response = await addNote(noteData);

      if (response?.status === "success") {
        await getNotes();
        setNoteModalStates((prev) => ({
          ...initialNoteStates,
          noteCategory: prev.noteCategory, // Keep the same category
        }));
        setShowAddNoteModal(false);

        toast.success("Note added successfully!");
        return response; // Return the response to be used by the AddNotesModal
      } else {
        // Type guard to check if response has a message property
        const errorMessage = (response as any)?.message || "Failed to add note";
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save note";
    }
  };

  const closeModal = () => {
    getNotes(true, "desc");
    setShowAddNoteModal(false);
    setNoteModalStates(initialNoteStates);
  };

  const handleViewNotesModal = () => {
    getNotes(true, "desc");
    setViewNotemodal({ status: false });
  };

  const truncateHtml = (html: string, maxLength: number = 150): string => {
    if (!html) return html;

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    const text = tempDiv.textContent || tempDiv.innerText || "";

    if (text.length <= maxLength) return html;
    let truncateAt = maxLength;
    const lastSpaceIndex = text.lastIndexOf(" ", maxLength);
    if (lastSpaceIndex > maxLength * 0.8) {
      truncateAt = lastSpaceIndex;
    }

    const truncatedText = text.substring(0, truncateAt);
    let result = "";
    let textCount = 0;
    let i = 0;
    const htmlLength = html.length;
    let insideTag = false;

    while (i < htmlLength && textCount < truncateAt) {
      const char = html[i];

      if (char === "<") {
        insideTag = true;
        result += char;
      } else if (char === ">") {
        insideTag = false;
        result += char;
      } else if (insideTag) {
        result += char;
      } else {
        if (textCount < truncateAt) {
          result += char;
          textCount++;
        }
      }

      i++;
    }

    result += "...";
    const openTags = [];
    const tagRegex = /<\/?([a-zA-Z0-9]+)[^>]*>/g;
    let match;
    tagRegex.lastIndex = 0;

    while ((match = tagRegex.exec(result)) !== null) {
      const tagName = match[1].toLowerCase();
      const isClosing = match[0].startsWith("</");

      if (isClosing) {
        const index = openTags.lastIndexOf(tagName);
        if (index !== -1) {
          openTags.splice(index, 1);
        }
      } else if (!match[0].endsWith("/>")) {
        openTags.push(tagName);
      }
    }
    for (let j = openTags.length - 1; j >= 0; j--) {
      result += `</${openTags[j]}>`;
    }

    return result;
  };

  const EmptyNotes = () => (
    <div className={styles.emptyNotes}>
      <svg>
        <use xlinkHref="/assets/sprite.svg#icon-notes-empty" />
      </svg>
      <p>No notes available</p>
      <span>Add a new note to get started</span>
    </div>
  );

  return (
    <CardWrapper>
      <div className={styles.noteSubHeader}>
        <div className={styles.title}>{title}</div>

        <Button
          span={false}
          label=" New Note"
          onClick={() => {
            setShowAddNoteModal(true);
            setTriggeredBy("New Note");
          }}
          submitButtonStyle={{
            fontSize: "1.4rem",
            fontWeight: 400,
            padding: "1rem",
          }}
          icon="icon-add"
        />
      </div>

      <div
        ref={notesContainerRef}
        className={styles.note_card_container}
        // onScroll={handleScroll}
      >
        {fetchingNotes && fetchedNotes.length === 0 ? (
          <>
            <NoteSkeleton count={3} />
          </>
        ) : fetchedNotes && fetchedNotes.length > 0 ? (
          <>
            {fetchedNotes.map((item, index) => (
              <CardWrapper
                key={item._id || index}
                customClass={styles.noteWrapper}
              >
                <div className={styles.noteHeader}>
                  <div className="flex">
                    {item.isImportant === "yes" && (
                      <svg className={classes.importantNoteIcon}>
                        <use xlinkHref="/assets/sprite.svg#grey-star-outline" />
                      </svg>
                    )}
                    <div
                      className={`${styles.noteType} ${
                        item.noteCategory === "clinical"
                          ? styles.noteType_clinical
                          : item.noteCategory === "non-clinical"
                          ? styles.noteType_non_clinical
                          : item.noteCategory === "timer-summary"
                          ? styles.noteType_timer_summary
                          : ""
                      }`}
                    >
                      <span>
                        {item.noteCategory === "non-clinical"
                          ? "Internal"
                          : item.noteCategory === "timer-summary"
                          ? "Timer Summary"
                          : item.noteCategory
                          ? item.noteCategory
                              .split("-")
                              .map(
                                (word) =>
                                  word.charAt(0).toUpperCase() +
                                  word.slice(1).toLowerCase()
                              )
                              .join("-")
                          : "N/A"}
                      </span>
                    </div>
                    <p className={styles.noteDate}>
                      {item.createdAt
                        ? new Date(item.createdAt).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                        : "-"}
                    </p>
                  </div>
                  <button
                    className={styles.iconButton}
                    onClick={() => {
                      setViewNotemodal({ status: true, data: item });
                    }}
                  >
                    <svg className={styles.iconEye}>
                      <use xlinkHref="/assets/sprite.svg#icon-eye" />
                    </svg>
                  </button>
                </div>

                {item.noteCategory === "clinical" && item.type && (
                  <p className={classes.note_type_display_card}>
                    {item.type
                      .split("-")
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1)
                      )
                      .join(" ")}
                  </p>
                )}
                <div
                  className={styles.noteValue}
                  dangerouslySetInnerHTML={{
                    __html:
                      truncateHtml(item.richText) || "No content available",
                  }}
                />
                {(item.createdBy?.name || item.clinicianId?.name) && (
                  <div className={styles.noteFooter}>
                    {item.createdBy?.name && (
                      <p className={styles.noteUpdatedBy}>
                        Created by: {item?.createdBy?.name}
                      </p>
                    )}
                    {item.clinicianId?.name && (
                      <p className={styles.noteUpdatedBy}>
                        Updated by: {item?.clinicianId?.name}
                      </p>
                    )}
                  </div>
                )}
              </CardWrapper>
            ))}
            {fetchingNotes && (
              <div className={styles.loadingMore}>
                <div className={styles.loadingSpinner}></div>
                <p className={styles.loadingText}>Loading more notes...</p>
              </div>
            )}
          </>
        ) : (
          <EmptyNotes />
        )}
      </div>

      <AddNotesModal
        addNotesModal={showAddNoteModal as boolean}
        saveNoteLoading={addNoteLoading}
        getNotesLoading={fetchingNotes}
        patientData={patientData}
        fetchedNotes={fetchedNotes}
        noteModalStates={noteModalStates}
        setNoteModalStates={setNoteModalStates}
        handleSubmit={handleAddNote}
        onCloseModal={closeModal}
        getNotes={getNotes}
        noteTypeOptions={noteTypeOptions}
        noteTypesLoading={noteTypesLoading}
        noteTypesError={noteTypesError || ""}
        triggeredBy={triggeredBy}
        billingSessionId={billingSessionId}
      />
      <ViewNotesModal
        ViewNotesModal={viewNoteModal}
        onCloseModal={handleViewNotesModal}
        handleSaveNote={handleSaveNote}
        patientId={patientData?._id}
        onNoteDeleted={() => getNotes(true, "desc")}
        noteModalStates={noteModalStates}
        setNoteModalStates={setNoteModalStates}
        saveNoteLoading={addNoteLoading}
        getNotes={getNotes}
        fetchedNotes={fetchedNotes}
        getNotesLoading={fetchingNotes}
        noteTypeOptions={noteTypeOptions}
        noteTypesLoading={noteTypesLoading}
        noteTypesError={noteTypesError || ""}
        patientData={patientData as IPatient}
        triggeredBy={triggeredBy}
      />
    </CardWrapper>
  );
};

export default NoteCard;
